/**
 * ПРАВА — единый источник правды про «что может текущий пользователь в этой комнате».
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ, а не room.canX() в каждом компоненте:
 * после добавления модерации проверок станет больше десятка. Размазать их по
 * кнопкам — значит гарантированно получить место, где кнопка видна, а сервер
 * отвечает 403. Здесь ОДИН объект, который компонент читает целиком:
 * нет флага — нет кнопки, точка.
 *
 * ИСТОЧНИК ДАННЫХ — m.room.power_levels (state event). SDK-методы
 * (currentState.maySendStateEvent / maySendRedactionForEvent, room.canInvite
 * и т.д.) читают его же, так что флаги согласованы с решением сервера.
 *
 * ВЕРСИЯ API (сверено с документацией matrix-js-sdk 42.3.0):
 *  - maySendStateEvent / maySendEvent / maySendRedactionForEvent живут на
 *    RoomState (room.currentState), а НЕ на Room;
 *  - Room.maySendMessage() не принимает userId — проверяет текущего клиента;
 *  - Room.canInvite(userId) есть, но для kick/ban готового метода Room нет:
 *    пороги читаем из content m.room.power_levels напрямую (см. topLevelFor).
 *
 * ОБНОВЛЕНИЕ: power levels меняются в любой момент. Хук useRoomPermissions
 * подписывается на RoomStateEvent.Events и пересчитывает объект.
 */
import { useEffect, useMemo, useState } from "react";
import {
  EventType,
  RoomStateEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from "matrix-js-sdk";

export interface RoomPermissions {
  // --- Участники -------------------------------------------------------
  /** Могу приглашать новых участников. */
  canInvite: boolean;
  /** Могу кикать вообще (порог kick). Конкретную цель проверять canActOnMember. */
  canKick: boolean;
  /** Могу банить/разбанивать вообще (порог ban). Цель — canActOnMember. */
  canBan: boolean;
  /** Могу менять m.room.power_levels (назначать роли). */
  canChangePowerLevels: boolean;

  // --- Настройки комнаты ------------------------------------------------
  canChangeName: boolean;
  canChangeTopic: boolean;
  canChangeAvatar: boolean;
  canChangeJoinRules: boolean;
  canChangeHistoryVisibility: boolean;

  // --- Сообщения ---------------------------------------------------------
  /** Могу писать в комнату. */
  canSendMessage: boolean;
  /**
   * Могу упомянуть ВСЮ комнату (@room). Порог — `notifications.room` из
   * m.room.power_levels, по умолчанию 50 (модератор). Адресные упоминания
   * (@имя) прав не требуют — только @room.
   */
  canMentionRoom: boolean;
  /**
   * Могу удалять ЧУЖИЕ сообщения (redact по праву модератора), порог
   * m.room.redaction. Свои — всегда можно (это право автора, не модератора),
   * для точной проверки конкретного события использовать
   * currentState.maySendRedactionForEvent(event, me).
   */
  canRedactOthers: boolean;

  /** Мой числовой уровень прав — нужен UI для сравнений «выше/ниже». */
  myPowerLevel: number;
}

const ALL_FALSE: RoomPermissions = {
  canInvite: false,
  canKick: false,
  canBan: false,
  canChangePowerLevels: false,
  canChangeName: false,
  canChangeTopic: false,
  canChangeAvatar: false,
  canChangeJoinRules: false,
  canChangeHistoryVisibility: false,
  canSendMessage: false,
  canMentionRoom: false,
  canRedactOthers: false,
  myPowerLevel: 0,
};

/** Сырой content m.room.power_levels (или undefined, если state ещё не пришёл). */
function powerLevelsContent(room: Room): Record<string, unknown> | undefined {
  return room.currentState
    .getStateEvents(EventType.RoomPowerLevels, "")
    ?.getContent() as Record<string, unknown> | undefined;
}

/**
 * Числовой порог из m.room.power_levels.
 *
 * Порядок по спеке: events[тип] ?? events_default ?? fallback. Для
 * kick/ban/redact/events(state) fallback = 50; для обычных сообщений — 0.
 */
function levelFor(
  pl: Record<string, unknown> | undefined,
  key: string,
  fallback: number,
): number {
  const events = (pl?.events ?? {}) as Record<string, number>;
  const v = events[key];
  if (typeof v === "number") return v;
  const d = pl?.events_default;
  if (typeof d === "number") return d;
  return fallback;
}

/** Верхнеуровневый порог из m.room.power_levels: kick/ban/invite и т.п. — свои ключи. */
function topLevelFor(
  pl: Record<string, unknown> | undefined,
  key: "kick" | "ban" | "invite" | "redact",
  fallback: number,
): number {
  const v = pl?.[key];
  return typeof v === "number" ? v : fallback;
}

/**
 * Порог на упоминание всей комнаты (@room).
 *
 * По спеке живет НЕ в events, а во вложенном объекте `notifications.room`
 * (по умолчанию 50 — модератор). Если `notifications` или `notifications.room`
 * отсутствуют в state, действует дефолт 50.
 */
function mentionRoomLevel(pl: Record<string, unknown> | undefined): number {
  const notifications = pl?.notifications as { room?: number } | undefined;
  return typeof notifications?.room === "number" ? notifications.room : 50;
}

/** Мой power level в комнате: users[me] ?? users_default ?? 0. */
export function myPowerLevelIn(client: MatrixClient, room: Room): number {
  const me = client.getUserId();
  if (!me) return 0;
  const pl = powerLevelsContent(room) as
    | { users?: Record<string, number>; users_default?: number }
    | undefined;
  return pl?.users?.[me] ?? pl?.users_default ?? 0;
}

/** Power level произвольного пользователя. */
export function powerLevelOf(room: Room, userId: string): number {
  const pl = powerLevelsContent(room) as
    | { users?: Record<string, number>; users_default?: number }
    | undefined;
  return pl?.users?.[userId] ?? pl?.users_default ?? 0;
}

/**
 * Могу ли я совершить модераторское действие над КОНКРЕТНЫМ участником.
 *
 * Правило спеки: мой PL строго ВЫШЕ PL цели, плюс нужен порог действия.
 * Равного по уровню кикнуть нельзя — иначе два модератора могли бы кикать
 * друг друга бесконечно. Над собой через эти кнопки не действуем вообще.
 */
export function canActOnMember(
  client: MatrixClient,
  room: Room,
  targetUserId: string,
  action: "kick" | "ban" | "changePowerLevel",
): boolean {
  const me = client.getUserId();
  if (!me || me === targetUserId) return false;
  const mine = myPowerLevelIn(client, room);
  const theirs = powerLevelOf(room, targetUserId);
  if (mine <= theirs) return false;

  const pl = powerLevelsContent(room);
  switch (action) {
    case "kick":
      return mine >= topLevelFor(pl, "kick", 50);
    case "ban":
      return mine >= topLevelFor(pl, "ban", 50);
    case "changePowerLevel":
      return mine >= levelFor(pl, EventType.RoomPowerLevels as string, 50);
  }
}

/**
 * Сводный объект прав. Чистая функция — тестируется без React.
 *
 * membership !== "join" → все флаги false: в приглашении мы ещё ничего не
 * можем, а в покинутой — тем более. Это же защита от «кнопка видна в
 * preview-комнате».
 */
export function getRoomPermissions(client: MatrixClient, room: Room): RoomPermissions {
  const me = client.getUserId();
  if (!me) return ALL_FALSE;

  const membership = room.getMyMembership();
  if (membership !== "join") return ALL_FALSE;

  const state = room.currentState;
  const myPl = myPowerLevelIn(client, room);
  const pl = powerLevelsContent(room);

  return {
    // Room.canInvite учитывает и join_rules, и порог invite — берём его.
    canInvite: room.canInvite(me),
    canKick: myPl >= topLevelFor(pl, "kick", 50),
    canBan: myPl >= topLevelFor(pl, "ban", 50),
    canChangePowerLevels: state.maySendStateEvent(EventType.RoomPowerLevels as string, me),

    canChangeName: state.maySendStateEvent(EventType.RoomName as string, me),
    canChangeTopic: state.maySendStateEvent(EventType.RoomTopic as string, me),
    canChangeAvatar: state.maySendStateEvent(EventType.RoomAvatar as string, me),
    canChangeJoinRules: state.maySendStateEvent("m.room.join_rules", me),
    canChangeHistoryVisibility: state.maySendStateEvent("m.room.history_visibility", me),

    // Room.maySendMessage() — это метод Room (а не RoomState): он проверяет
    // и порог отправки, и моё членство в комнате, что надёжнее RoomState-варианта,
    // который смотрит только на power level. Для модераторских действий ниже
    // membership уже проверен выше, но лишняя проверка не мешает.
    canSendMessage: room.maySendMessage(),
    canMentionRoom: myPl >= mentionRoomLevel(pl),
    canRedactOthers: myPl >= levelFor(pl, "m.room.redaction", 50),

    myPowerLevel: myPl,
  };
}

/**
 * Могу ли я удалить КОНКРЕТНОЕ сообщение. Тонкая обёртка над
 * currentState.maySendRedactionForEvent: SDK сам различает «своё» (автор
 * всегда может) и «чужое» (по порогу m.room.redaction). Компонентам нужна
 * именно эта функция, а не грубый canRedactOthers.
 */
export function canRedactEvent(
  client: MatrixClient,
  room: Room,
  event: MatrixEvent,
): boolean {
  const me = client.getUserId();
  if (!me) return false;
  return room.currentState.maySendRedactionForEvent(event, me);
}

/**
 * React-хук: объект прав, пересчитываемый при изменении state комнаты.
 *
 * Слушаем RoomStateEvent.Events (любые state events: power_levels,
 * join_rules, имя…) и Members (изменение моего membership меняет всё).
 * Фильтровать по типу не стоит: join_rules влияет на canInvite внутри SDK,
 * и перечислять влияющие типы — верный способ однажды забыть один.
 */
export function useRoomPermissions(client: MatrixClient, room: Room): RoomPermissions {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    client.on(RoomStateEvent.Events, bump);
    client.on(RoomStateEvent.Members, bump);
    return () => {
      client.off(RoomStateEvent.Events, bump);
      client.off(RoomStateEvent.Members, bump);
    };
  }, [client]);

  return useMemo(
    () => getRoomPermissions(client, room),
    // tick — сигнал «state комнаты изменился», пересчитываем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, room, tick],
  );
}
